import React from 'react';
import { NavLink } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useLang, SUPPORTED_LANGS } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { NGRPLogoMark, Container, TricolourRibbon } from '@/components/common/Layout';

const LANG_LABELS = { en: 'EN', hi: 'हिं', mr: 'मराठी' };

function LanguageToggle() {
  const { lang, setLang, t } = useLang();
  return (
    <div
      role="radiogroup"
      aria-label={t('common.language', 'Language')}
      data-testid="language-toggle"
      className="inline-flex items-center rounded-full border border-[#D7E1EF] bg-white p-0.5"
    >
      {SUPPORTED_LANGS.map((l) => {
        const active = lang === l;
        return (
          <button
            key={l}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`language-toggle-${l}`}
            onClick={() => setLang(l)}
            className={[
              'text-[11px] sm:text-xs font-medium px-2.5 py-1 rounded-full transition-colors',
              active ? 'bg-[#062B5C] text-white' : 'text-[#062B5C] hover:bg-[#F5F8FC]',
            ].join(' ')}
          >
            {LANG_LABELS[l]}
          </button>
        );
      })}
    </div>
  );
}

function NavLinks({ onNavigate }) {
  const { t } = useLang();
  const items = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/founder', label: t('nav.founder') },
    { to: '/national', label: t('nav.national') },
    { to: '/vision', label: t('nav.vision') },
    { to: '/emblem', label: t('nav.emblem') },
    { to: '/constitution', label: t('nav.constitution') },
    { to: '/roadmap', label: t('nav.roadmap') },
    { to: '/dialogue', label: t('nav.dialogue') },
    { to: '/agenda', label: t('nav.agenda') },
    { to: '/dashboard', label: t('nav.dashboard') },
    { to: '/manifesto', label: t('nav.manifesto') },
    { to: '/aspirant', label: t('nav.aspirant') },
    { to: '/news', label: t('nav.news') },
    { to: '/transparency', label: t('nav.transparency') },
    { to: '/contact', label: t('nav.contact') },
  ];
  return (
    <>
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to === '/'}
          onClick={onNavigate}
          data-testid={`nav-link-${it.to.replace('/', '') || 'home'}`}
          className={({ isActive }) =>
            `nav-link text-[13px] lg:text-sm whitespace-nowrap ${isActive ? 'active' : ''}`
          }
        >
          {it.label}
        </NavLink>
      ))}
    </>
  );
}

export function Header() {
  const { t } = useLang();
  const [open, setOpen] = React.useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-[#D7E1EF] bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75">
      <TricolourRibbon />
      <Container className="h-16 flex items-center justify-between gap-4">
        <NavLink to="/" className="flex items-center gap-3 min-w-0 shrink-0" data-testid="header-logo-link">
          <NGRPLogoMark size={48} />
          <div className="leading-tight min-w-0 hidden sm:block">
            <div className="font-display font-semibold text-[13px] lg:text-[15px] text-[#062B5C] whitespace-nowrap">
              {t('common.party_name_full')}
            </div>
            <div className="text-[11px] text-[#5B6B82] font-medium tracking-wide">
              {t('common.party_short')}
            </div>
          </div>
          <div className="leading-tight sm:hidden">
            <div className="font-display font-semibold text-sm text-[#062B5C]">{t('common.party_short')}</div>
          </div>
        </NavLink>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageToggle />
          <NavLink to="/join" className="hidden sm:block">
            <Button data-testid="header-join-cta" size="sm" className="bg-[#062B5C] hover:bg-[#05244d] text-white">
              {t('common.join_cta')}
            </Button>
          </NavLink>

          {/* Mobile hamburger */}
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Open menu"
                  data-testid="mobile-nav-open-button"
                >
                  <Menu className="h-5 w-5 text-[#062B5C]" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] sm:w-[380px]">
                <div className="flex flex-col gap-4 pt-6">
                  <div className="flex items-center gap-3">
                    <NGRPLogoMark size={32} />
                    <div className="font-display text-sm font-semibold text-[#062B5C]">
                      {t('common.party_name_full')}
                    </div>
                  </div>
                  <nav className="flex flex-col gap-3 mt-4">
                    <NavLinks onNavigate={() => setOpen(false)} />
                  </nav>
                  <NavLink to="/join" onClick={() => setOpen(false)} className="mt-4">
                    <Button className="w-full bg-[#062B5C] hover:bg-[#05244d] text-white">
                      {t('common.join_cta')}
                    </Button>
                  </NavLink>
                  <NavLink to="/aspirant" onClick={() => setOpen(false)}>
                    <Button variant="outline" className="w-full border-[#D7E1EF] text-[#062B5C]">
                      {t('nav.aspirant')}
                    </Button>
                  </NavLink>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </Container>

      {/* Primary nav bar */}
      <div className="hidden lg:block border-t border-[#D7E1EF] bg-white">
        <Container className="h-11 flex items-center gap-6 overflow-x-auto">
          <NavLinks />
        </Container>
      </div>
    </header>
  );
}
